import { PlasFacet, PlasFacetTerm } from "@biblioteksentralen/bmdb-search-client";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Checkbox,
  CheckboxGroup,
  HideWithCSS,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  VStack,
} from "@biblioteksentralen/react";
import { ChevronDown } from "react-feather";
import { Translate, useTranslation } from "../../../utils/hooks/useTranslation";
import { useFacetAndFilterContext } from "./FacetAndFilterContext";
import { WorkSearchFacet } from "./types";
import { useSearchState } from "./useSearchState";

const getFacetLabel = (facet: PlasFacet, t: Translate["t"]) => {
  if (facet.type === "plas.audience.ageGroup") return t("Målgruppe");
  if (facet.type === "plas.audience.ageRange") return t("Aldersspenn");
  if (facet.type === "bmdb.medium") return t("Format");
  return facet.name;
};

const WorkSearchFacets = ({
  onChange,
}: {
  onChange: (checked: boolean, facet: PlasFacet, term: PlasFacetTerm) => void;
}) => {
  const { cachedRelevantFacets, cacheRelevantFacets: cacheRelevantFacets } = useFacetAndFilterContext();

  return (
    <>
      <HideWithCSS above="md" width="100%">
        <Accordion as="ul" allowToggle listStyleType="none" onChange={cacheRelevantFacets}>
          {cachedRelevantFacets?.map((facet) => {
            return <FilterInput key={facet.type} facet={facet} onChange={onChange} menuOrAccordion="accordion" />;
          })}
        </Accordion>
      </HideWithCSS>
      <HideWithCSS below="md" width="100%">
        <HStack gap="0.5rem" flexWrap="wrap">
          {cachedRelevantFacets?.map((facet, index) => {
            return <FilterInput key={facet.type} facet={facet} onChange={onChange} menuOrAccordion="menu" />;
          })}
        </HStack>
      </HideWithCSS>
    </>
  );
};

type FilterInputProps = {
  facet: PlasFacet;
  onChange: (checked: boolean, facet: PlasFacet, term: PlasFacetTerm) => void;
  menuOrAccordion: "menu" | "accordion";
};

const FilterInput = ({ facet, onChange, menuOrAccordion }: FilterInputProps) => {
  const [searchState] = useSearchState();
  const {
    cachedCurrentFacet,
    setCachedCurrentFacet,
    cacheRelevantFacets: setCachedFacets,
  } = useFacetAndFilterContext();
  const { t } = useTranslation();
  const facetToUse = cachedCurrentFacet?.type === facet.type ? cachedCurrentFacet : facet;

  if (menuOrAccordion === "menu")
    return (
      <Popover placement="bottom-start" onClose={setCachedFacets} onOpen={setCachedFacets}>
        <PopoverTrigger>
          <Button size="md" variant="outline" rightIcon={<ChevronDown />}>
            {getFacetLabel(facet, t)}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          width="12rem"
          padding=".2rem" // Litt padding så man ser fokusringen ved tabbing
          maxH="20rem"
          overflow="auto"
        >
          <PopoverBody>
            <Stack direction="column">
              {facetToUse.terms.map((term) => (
                <Checkbox
                  key={term.value}
                  isChecked={!!searchState?.work?.[facet.type as WorkSearchFacet]?.includes(term.value)}
                  onChange={(event) => {
                    setCachedCurrentFacet && setCachedCurrentFacet(facetToUse);
                    onChange(event.target.checked, facetToUse, term);
                  }}
                >
                  <Text fontSize=".9rem">{term.name}</Text>
                </Checkbox>
              ))}
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    );

  if (menuOrAccordion === "accordion")
    return (
      <AccordionItem as="li" key={facet.type}>
        <>
          <AccordionButton padding=".5rem .75rem" justifyContent="space-between">
            <Text marginY=".25rem" size="1rem" fontWeight={600}>
              {getFacetLabel(facet, t)}
            </Text>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel padding="0 1rem 1rem">
            <CheckboxGroup>
              <VStack as="ul" alignItems="flex-start">
                {facetToUse.terms.map((term) => {
                  return (
                    <Checkbox
                      key={term.name}
                      as="li"
                      isChecked={!!searchState?.work?.[facet.type as WorkSearchFacet]?.includes(term.value)}
                      onChange={(event) => {
                        setCachedCurrentFacet && setCachedCurrentFacet(facetToUse);
                        onChange(event.target.checked, facetToUse, term);
                      }}
                    >
                      {term.name}
                    </Checkbox>
                  );
                })}
              </VStack>
            </CheckboxGroup>
          </AccordionPanel>
        </>
      </AccordionItem>
    );
};

export default WorkSearchFacets;
